import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
    LayoutDashboard,
    Package,
    FileText,
    Users,
    ShoppingCart,
    Shield,
    LifeBuoy,
    Clock,
    X
} from 'lucide-react';

interface SidebarProps {
    isOpen: boolean;
    onClose: () => void;
}

interface NavItem {
    name: string;
    path: string;
    icon: React.ReactNode;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
    const location = useLocation();

    const navItems: NavItem[] = [
        { name: 'Dashboard', path: '/dashboard', icon: <LayoutDashboard className="w-5 h-5" /> },
        { name: 'Lockers', path: '/lockers', icon: <Package className="w-5 h-5" /> },
        { name: 'Leases', path: '/leases', icon: <FileText className="w-5 h-5" /> },
        { name: 'Pending Finalisations', path: '/pending-finalisations', icon: <Clock className="w-5 h-5" /> },
        { name: 'Customers', path: '/customers', icon: <Users className="w-5 h-5" /> },
        { name: 'Products', path: '/products', icon: <ShoppingCart className="w-5 h-5" /> },
        { name: 'Admin Management', path: '/admin-management', icon: <Shield className="w-5 h-5" /> },
        { name: 'Support', path: '/support', icon: <LifeBuoy className="w-5 h-5" /> },
    ];

    const isActive = (path: string) => {
        if (path === '/dashboard' && location.pathname === '/') return true;
        return location.pathname === path || location.pathname === path + '-mock';
    };

    return (
        <>
            {/* Mobile backdrop */}
            {isOpen && (
                <div 
                    className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                    onClick={onClose}
                />
            )}
            
            {/* Sidebar */}
            <div className={`
                fixed top-16 left-0 h-[calc(100vh-4rem)] w-64 bg-slate-900 text-white shadow-2xl z-40
                transform transition-transform duration-300 ease-in-out
                ${isOpen ? 'translate-x-0' : '-translate-x-full'} 
                lg:translate-x-0
            `}>
                {/* Mobile close button */}
                <div className="flex items-center justify-between p-4 lg:hidden">
                    <h2 className="text-xl font-bold">Menu</h2>
                    <button
                        onClick={onClose}
                        className="p-2 rounded-lg hover:bg-slate-800 transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="flex flex-col h-full sidebar-scroll">
                    {/* Logo/Brand */}
                    <div className="p-6 border-b border-slate-800 hidden lg:block flex-shrink-0">
                        <h2 className="text-2xl font-bold bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent">
                            Lease Admin
                        </h2>
                        <p className="text-slate-400 text-sm mt-1">Management Portal</p>
                    </div>

                    {/* Navigation */}
                    <nav className="flex-1 p-4">
                        <ul className="space-y-2">
                            {navItems.map((item) => (
                                <li key={item.path}>
                                    <Link
                                        to={item.path}
                                        onClick={onClose}
                                        className={`
                                            flex items-center gap-3 px-4 py-3 rounded-lg
                                            transition-all duration-200 group
                                            ${isActive(item.path)
                                                ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-600/20'
                                                : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                                            }
                                        `}
                                    >
                                        <span className={`
                                            transition-transform duration-200
                                            ${isActive(item.path) ? 'scale-110' : 'group-hover:scale-110'}
                                        `}>
                                            {item.icon}
                                        </span>
                                        <span className="font-medium">{item.name}</span>
                                        {isActive(item.path) && (
                                            <div className="ml-auto w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
                                        )}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </nav>

                </div>
            </div>
        </>
    );
};

export default Sidebar;